/**
 * Step 3: Map Columns
 *
 * Shows each schema field alongside a dropdown to select which spreadsheet
 * column it maps to. Auto-matched columns are pre-selected.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useTranslation } from 'react-i18next';
import { ColumnMapping, ContextDefault, UploadFieldDef } from '../utils/types';
import { getMatchStats } from '../utils/autoMatch';

interface StepMapColumnsProps {
	mappings: ColumnMapping[];
	fields: UploadFieldDef[];
	headers: string[];
	onUpdateMapping: (targetField: string, sourceColumn: string | null) => void;
	contextDefaults: ContextDefault[];
}

export const StepMapColumns: React.FC<StepMapColumnsProps> = ({
	mappings,
	fields,
	headers,
	onUpdateMapping,
	contextDefaults,
}) => {
	const { t } = useTranslation();
	const stats = getMatchStats(mappings, fields);

	// Columns already used by another mapping
	const usedColumns = new Set(
		mappings.map((m) => m.sourceColumn).filter((c): c is string => c !== null)
	);

	return (
		<Box>
			{/* Summary */}
			<Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
				<Chip
					icon={<AutorenewIcon />}
					label={t('bulkUpload.map.mapped', { matched: stats.matched, total: stats.total })}
					color={stats.matched === stats.total ? 'success' : 'default'}
					size="small"
				/>
				{stats.requiredMissing > 0 && (
					<Chip
						icon={<ErrorIcon />}
						label={t('bulkUpload.map.requiredUnmapped', { count: stats.requiredMissing })}
						color="error"
						size="small"
					/>
				)}
			</Box>

			{/* Context defaults info */}
			{contextDefaults.length > 0 && (
				<Alert severity="info" sx={{ mb: 2 }}>
					<Typography variant="body2">
						{t('bulkUpload.map.contextFieldsNote', { count: contextDefaults.length })}
					</Typography>
				</Alert>
			)}

			{/* Mapping table */}
			<TableContainer sx={{ maxHeight: 400 }}>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 'bold', width: '35%' }}>
								{t('bulkUpload.map.schemaField')}
							</TableCell>
							<TableCell sx={{ fontWeight: 'bold', width: '45%' }}>
								{t('bulkUpload.map.spreadsheetColumn')}
							</TableCell>
							<TableCell sx={{ fontWeight: 'bold', width: '20%' }}>{t('status')}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{fields.map((field) => {
							const mapping = mappings.find((m) => m.targetField === field.name);
							const hasContextDefault = contextDefaults.some((cd) => cd.targetField === field.name);
							const isMapped = mapping?.sourceColumn !== null;

							return (
								<TableRow
									key={field.name}
									sx={{
										bgcolor:
											field.required && !isMapped && !hasContextDefault ? 'error.light' : undefined,
										'& > td': { opacity: hasContextDefault && !isMapped ? 0.6 : 1 },
									}}>
									<TableCell>
										<Typography
											variant="body2"
											sx={{ fontWeight: field.required ? 'bold' : 'normal' }}>
											{field.label}
											{field.required && (
												<Typography component="span" color="error" sx={{ ml: 0.5 }}>
													*
												</Typography>
											)}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{field.dataType}
											{field.dataType === 'relation' && field.relation
												? ` → ${field.relation.entityType}`
												: ''}
										</Typography>
									</TableCell>
									<TableCell>
										<FormControl fullWidth size="small">
											<Select
												value={mapping?.sourceColumn ?? ''}
												displayEmpty
												onChange={(e) =>
													onUpdateMapping(field.name, e.target.value === '' ? null : e.target.value)
												}>
												<MenuItem value="">
													<em>
														{hasContextDefault
															? t('bulkUpload.map.usingContextDefault')
															: t('bulkUpload.map.notMapped')}
													</em>
												</MenuItem>
												{headers.map((h) => (
													<MenuItem
														key={h}
														value={h}
														disabled={usedColumns.has(h) && mapping?.sourceColumn !== h}>
														{h}
														{usedColumns.has(h) &&
															mapping?.sourceColumn !== h &&
															` ${t('bulkUpload.map.used')}`}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</TableCell>
									<TableCell>
										{isMapped ? (
											<Chip
												icon={<CheckCircleIcon />}
												label={
													mapping?.autoMatched
														? t('bulkUpload.map.auto')
														: t('bulkUpload.map.manual')
												}
												color="success"
												size="small"
												variant="outlined"
											/>
										) : hasContextDefault ? (
											<Chip
												label={t('bulkUpload.map.context')}
												color="info"
												size="small"
												variant="outlined"
											/>
										) : field.required ? (
											<Chip icon={<ErrorIcon />} label={t('required')} color="error" size="small" />
										) : (
											<Typography variant="caption" color="text.secondary">
												{t('bulkUpload.map.optional')}
											</Typography>
										)}
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};
